/**
 * Settings Page
 * Account settings, preferences, and logout
 */

import {
	ArrowLeft,
	Globe,
	KeyRound,
	LogOut,
	Shield,
	Smartphone,
	SunMoon,
	User,
	Wallet,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SkeletonRows } from "@/components/ui/skeleton";
import { CollectPhoneModal } from "@/features/auth/components/collect-phone-modal";
import { useLogout } from "@/features/auth/hooks/use-logout";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { DeviceSessions } from "../components/device-sessions";
import { LanguageSwitcher } from "../components/language-switcher";
import { SessionReplayToggle } from "../components/session-replay-toggle";
import { ThemeModeSwitcher } from "../components/theme-mode-switcher";
import {
	usePayoutAccount,
	useSavePayoutAccount,
} from "../hooks/use-payout-account";

export function SettingsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { hash, key: locationKey } = useLocation();
	const { user } = useAuthStore();
	const { logout: handleLogout, isLoggingOut } = useLogout();
	const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
	const [phoneModalOpen, setPhoneModalOpen] = useState(false);

	// Payout account (designers/contractors only) — server state via React Query.
	const isProfessional =
		user?.userType === "designer" || user?.userType === "contractor";
	const payoutQuery = usePayoutAccount(isProfessional);
	const payoutAccount = payoutQuery.data ?? null;
	const payoutLoading = payoutQuery.isLoading;
	const payoutLoadError = payoutQuery.isError;
	const savePayout = useSavePayoutAccount();
	const payoutSaving = savePayout.isPending;
	const [payoutEditing, setPayoutEditing] = useState(false);
	const [payoutForm, setPayoutForm] = useState({
		accountHolderName: "",
		accountNumber: "",
		ifscCode: "",
	});
	const [ifscError, setIfscError] = useState("");

	// `/settings#payout` (the "Add payout account" links on the dashboards and
	// the project page) scrolls to the payout section once the payout query
	// settles, and opens the form when the query succeeded with no account. It
	// runs once per navigation (location.key), so the user menu's "Payout
	// account" link scrolls again on every use, while cancelling the form or
	// saving an account, which do not navigate, does not reopen it.
	//
	// The scroll waits for the render that opens the form: scrollIntoView
	// measures the page when it is called, and before the form is in it the
	// page is shorter, so a section near the bottom could not reach the top.
	const payoutSectionRef = useRef<HTMLElement>(null);
	const payoutDeepLinkHandledKey = useRef<string | null>(null);
	const [payoutScrollPending, setPayoutScrollPending] = useState(false);
	useEffect(() => {
		if (hash !== "#payout" || payoutDeepLinkHandledKey.current === locationKey)
			return;
		if (payoutQuery.isPending) return;
		payoutDeepLinkHandledKey.current = locationKey;
		if (payoutQuery.isSuccess && !payoutQuery.data) setPayoutEditing(true);
		setPayoutScrollPending(true);
	}, [
		hash,
		locationKey,
		payoutQuery.isPending,
		payoutQuery.isSuccess,
		payoutQuery.data,
	]);
	useEffect(() => {
		if (!payoutScrollPending) return;
		setPayoutScrollPending(false);
		payoutSectionRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}, [payoutScrollPending]);

	const payoutSyncStatus = payoutAccount?.syncStatus ?? "pending";
	const payoutSyncLabel =
		payoutSyncStatus === "synced"
			? t("project.payments.syncSynced")
			: t("project.payments.syncPending");
	const payoutSyncClassName =
		payoutSyncStatus === "synced"
			? "bg-dashboard-pill-done-bg text-dashboard-success-strong"
			: "bg-dashboard-pill-awaiting-bg text-dashboard-warning";

	const validateIfsc = (code: string) => {
		const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
		if (code && !ifscRegex.test(code.toUpperCase())) {
			setIfscError(`${t("project.payments.ifscCode")} format: XXXX0XXXXXX`);
			return false;
		}
		setIfscError("");
		return true;
	};

	const handlePayoutSave = async () => {
		if (!validateIfsc(payoutForm.ifscCode)) return;
		try {
			const formData = {
				...payoutForm,
				ifscCode: payoutForm.ifscCode.toUpperCase(),
			};
			const result = await savePayout.mutateAsync({
				id: payoutAccount?.id,
				data: formData,
			});

			if (result.success && result.payoutAccount) {
				toast.success(t("project.payments.accountSaved"));
				setPayoutEditing(false);
			} else if (result.fieldErrors?.ifscCode) {
				// Field-specific: shown under the IFSC input, not as a toast the
				// user has to re-match to a field themselves.
				setIfscError(result.fieldErrors.ifscCode);
			} else {
				toast.error(
					result.error || t("common.error", "Failed to save account"),
				);
			}
		} catch {
			toast.error(t("common.error", "Failed to save account"));
		}
	};

	const startEditingPayout = () => {
		if (payoutAccount) {
			setPayoutForm({
				accountHolderName: payoutAccount.accountHolderName,
				accountNumber: "",
				ifscCode: payoutAccount.ifscCode,
			});
		}
		setPayoutEditing(true);
	};

	const userTypeLabel = () => {
		switch (user?.userType) {
			case "homeowner":
				return t("userTypes.homeowner");
			case "designer":
				return t("userTypes.designer");
			case "contractor":
				return t("userTypes.contractor");
			case "realtor":
				return t("userTypes.realtor");
			default:
				return t("userTypes.user");
		}
	};

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-2xl space-y-6">
				{/* Top Masthead Panel */}
				<FloatPanel elevation={1} padding={20}>
					<div className="flex items-center gap-3">
						<StudioButton
							variant="quiet"
							size="sm"
							onClick={() =>
								navigate(
									user?.userType === "homeowner" ? "/home" : "/dashboard",
								)
							}
							icon={ArrowLeft}
						>
							{t("common.back")}
						</StudioButton>
						<h1
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 28,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.02em",
							}}
						>
							{user?.userType === "designer"
								? t("settings.headingStudio")
								: t("settings.headingAccount")}{" "}
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									color: "var(--color-dashboard-accent-display)",
								}}
							>
								{t("settings.headingAccent")}
							</span>
							.
						</h1>
					</div>
				</FloatPanel>

				{/* Account Section */}
				<FloatPanel elevation={1} padding={20}>
					<div className="space-y-4">
						<div className="flex items-center gap-2 border-b border-[var(--color-dashboard-border)] pb-3">
							<User className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 16,
									color: "var(--color-dashboard-text)",
								}}
							>
								{t("settings.account")}
							</h3>
						</div>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm font-medium text-[var(--color-dashboard-text)]">
									{t("settings.accountType")}
								</p>
								<p className="text-sm text-muted-foreground">
									{userTypeLabel()}
								</p>
							</div>
						</div>
						<div className="pt-2">
							<StudioButton
								variant="quiet"
								size="sm"
								onClick={() => navigate("/profile/edit")}
								className="w-full sm:w-auto"
							>
								{t("settings.editProfile")}
							</StudioButton>
						</div>
					</div>
				</FloatPanel>

				{/* Phone Section */}
				<FloatPanel elevation={1} padding={20}>
					<div className="space-y-4">
						<div className="flex items-center gap-2 border-b border-[var(--color-dashboard-border)] pb-3">
							<Smartphone className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 16,
									color: "var(--color-dashboard-text)",
								}}
							>
								{t("settings.phone", "Phone")}
							</h3>
						</div>
						{user?.phone ? (
							<div className="flex items-center justify-between">
								<p className="text-sm font-medium text-[var(--color-dashboard-text)]">
									{user.phone}
								</p>
								<StudioButton
									variant="quiet"
									size="sm"
									onClick={() => setPhoneModalOpen(true)}
								>
									Edit
								</StudioButton>
							</div>
						) : (
							<div className="space-y-3">
								<p className="text-sm text-muted-foreground">
									Add a phone number to receive project updates via SMS.
								</p>
								<StudioButton
									variant="primary"
									size="sm"
									onClick={() => setPhoneModalOpen(true)}
								>
									Add phone number
								</StudioButton>
							</div>
						)}
					</div>
				</FloatPanel>

				{/* Language Section */}
				<FloatPanel elevation={1} padding={20}>
					<div className="space-y-4">
						<div className="flex items-center gap-2 border-b border-[var(--color-dashboard-border)] pb-3">
							<Globe className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 16,
									color: "var(--color-dashboard-text)",
								}}
							>
								{t("settings.language")}
							</h3>
						</div>
						<p className="text-sm text-muted-foreground">
							{t("settings.selectLanguage")}
						</p>
						<LanguageSwitcher />
					</div>
				</FloatPanel>

				{/* Appearance Section */}
				<FloatPanel elevation={1} padding={20}>
					<div className="space-y-4">
						<div className="flex items-center gap-2 border-b border-dashboard-border pb-3">
							<SunMoon className="h-5 w-5 text-dashboard-accent-dark" />
							<h3
								className="text-dashboard-text"
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 16,
								}}
							>
								{t("settings.appearance")}
							</h3>
						</div>
						<p className="text-sm text-muted-foreground">
							{t("settings.selectAppearance")}
						</p>
						<ThemeModeSwitcher />
					</div>
				</FloatPanel>

				{/* Syndication API keys (realtors only) */}
				{user?.userType === "realtor" && (
					<FloatPanel elevation={1} padding={20}>
						<div className="space-y-4">
							<div className="flex items-center gap-2 border-b border-[var(--color-dashboard-border)] pb-3">
								<KeyRound className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
								<h3
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 600,
										fontSize: 16,
										color: "var(--color-dashboard-text)",
									}}
								>
									{t("realtor.settings.title")}
								</h3>
							</div>
							<p className="text-sm text-muted-foreground">
								{t("settings.apiKeysDescription")}
							</p>
							<StudioButton
								variant="quiet"
								size="sm"
								onClick={() => navigate("/realtor/settings")}
								className="w-full sm:w-auto"
							>
								{t("settings.manageApiKeys")}
							</StudioButton>
						</div>
					</FloatPanel>
				)}

				{/* Privacy Section */}
				<FloatPanel elevation={1} padding={20}>
					<div className="space-y-4">
						<div className="flex items-center gap-2 border-b border-[var(--color-dashboard-border)] pb-3">
							<Shield className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 16,
									color: "var(--color-dashboard-text)",
								}}
							>
								{t("settings.privacy")}
							</h3>
						</div>
						<p className="text-sm text-muted-foreground">
							{t("settings.privacyMessage")}
						</p>
						<SessionReplayToggle />
					</div>
				</FloatPanel>

				{/* Devices Section */}
				<FloatPanel elevation={1} padding={20}>
					<DeviceSessions />
				</FloatPanel>

				{/* Payout Account Section (designers/contractors only) */}
				{isProfessional && (
					<section id="payout" ref={payoutSectionRef}>
						<FloatPanel elevation={1} padding={20}>
							<div className="space-y-4">
								<div className="flex items-center gap-2 border-b border-[var(--color-dashboard-border)] pb-3">
									<Wallet className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
									<h3
										style={{
											fontFamily: "var(--font-display)",
											fontWeight: 600,
											fontSize: 16,
											color: "var(--color-dashboard-text)",
										}}
									>
										{t("project.payments.payoutAccount")}
									</h3>
								</div>
								<p className="text-sm text-muted-foreground">
									{t("project.payments.payoutAccountDesc")}
								</p>

								{payoutLoading ? (
									<SkeletonRows label="Loading payout account" rows={2} />
								) : payoutAccount && !payoutEditing ? (
									<div className="space-y-4">
										<div className="rounded-xl border border-[var(--color-dashboard-border)] bg-[var(--color-dashboard-paper)] p-4 space-y-2">
											<div className="flex items-center justify-between">
												<p className="text-sm font-semibold text-[var(--color-dashboard-text)]">
													{payoutAccount.accountHolderName}
												</p>
												<span
													className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${payoutSyncClassName}`}
												>
													{payoutSyncLabel}
												</span>
											</div>
											{/* data-sensitive: masked in session replay. A full
											    bank account number, on the same page as the
											    recording toggle. See lib/posthog.ts. */}
											<p
												data-sensitive
												className="text-sm text-muted-foreground"
											>
												A/C: {payoutAccount.accountNumber}
											</p>
											<p
												data-sensitive
												className="text-sm text-muted-foreground"
											>
												IFSC: {payoutAccount.ifscCode}
											</p>
											{payoutSyncStatus !== "synced" && (
												<p className="text-sm text-dashboard-pill-awaiting">
													{t("project.payments.payoutRequiredBeforeRelease")}
												</p>
											)}
										</div>
										<StudioButton
											variant="quiet"
											size="sm"
											onClick={startEditingPayout}
										>
											{t("project.payments.editAccount")}
										</StudioButton>
									</div>
								) : (
									<div className="space-y-4">
										{payoutLoadError && !payoutEditing && (
											<div className="text-center py-4">
												<p className="text-sm text-dashboard-pill-risk mb-4">
													{t("project.payments.payoutAccountLoadError")}
												</p>
												<StudioButton
													variant="quiet"
													size="sm"
													onClick={() => payoutQuery.refetch()}
												>
													{t("project.payments.retryLoadPayoutAccount")}
												</StudioButton>
											</div>
										)}
										{!payoutAccount && !payoutLoadError && !payoutEditing && (
											<div className="text-center py-4">
												<p className="text-sm text-muted-foreground mb-4">
													{t("project.payments.noPayoutAccount")}
												</p>
												<StudioButton
													variant="quiet"
													size="sm"
													onClick={() => setPayoutEditing(true)}
												>
													{t("project.payments.saveAccount")}
												</StudioButton>
											</div>
										)}
										{payoutEditing && (
											<div className="space-y-4">
												<div className="space-y-1.5">
													<span
														style={{
															display: "block",
															fontSize: 13,
															fontWeight: 500,
															color: "var(--color-dashboard-muted-dark)",
														}}
													>
														{t("project.payments.accountHolderName")}
													</span>
													<input
														value={payoutForm.accountHolderName}
														onChange={(e) =>
															setPayoutForm({
																...payoutForm,
																accountHolderName: e.target.value,
															})
														}
														placeholder="Full name as on bank account"
														style={{
															width: "100%",
															padding: "10px 14px",
															border: "1px solid var(--color-border-default)",
															borderRadius: 7,
															background: "var(--color-dashboard-paper)",
															fontSize: 14,
															color: "var(--color-dashboard-text)",
															outline: "none",
														}}
													/>
												</div>
												<div className="space-y-1.5">
													<span
														style={{
															display: "block",
															fontSize: 13,
															fontWeight: 500,
															color: "var(--color-dashboard-muted-dark)",
														}}
													>
														{t("project.payments.accountNumber")}
													</span>
													<input
														value={payoutForm.accountNumber}
														onChange={(e) =>
															setPayoutForm({
																...payoutForm,
																accountNumber: e.target.value,
															})
														}
														placeholder={
															payoutAccount
																? "Enter new account number"
																: "Bank account number"
														}
														style={{
															width: "100%",
															padding: "10px 14px",
															border: "1px solid var(--color-border-default)",
															borderRadius: 7,
															background: "var(--color-dashboard-paper)",
															fontSize: 14,
															color: "var(--color-dashboard-text)",
															outline: "none",
														}}
													/>
												</div>
												<div className="space-y-1.5">
													<span
														style={{
															display: "block",
															fontSize: 13,
															fontWeight: 500,
															color: "var(--color-dashboard-muted-dark)",
														}}
													>
														{t("project.payments.ifscCode")}
													</span>
													<input
														value={payoutForm.ifscCode}
														onChange={(e) => {
															setPayoutForm({
																...payoutForm,
																ifscCode: e.target.value,
															});
															if (ifscError) validateIfsc(e.target.value);
														}}
														placeholder="e.g. SBIN0001234"
														style={{
															width: "100%",
															padding: "10px 14px",
															border: `1px solid ${ifscError ? "var(--color-dashboard-pill-risk)" : "var(--color-border-default)"}`,
															borderRadius: 7,
															background: "var(--color-dashboard-paper)",
															fontSize: 14,
															color: "var(--color-dashboard-text)",
															outline: "none",
														}}
													/>
													{ifscError && (
														<p className="text-xs text-dashboard-pill-risk">
															{ifscError}
														</p>
													)}
												</div>
												<div className="flex gap-3">
													<StudioButton
														variant="quiet"
														size="sm"
														onClick={() => {
															setPayoutEditing(false);
															setIfscError("");
														}}
														disabled={payoutSaving}
													>
														{t("common.cancel")}
													</StudioButton>
													<StudioButton
														variant="primary"
														size="sm"
														onClick={handlePayoutSave}
														disabled={
															payoutSaving ||
															!payoutForm.accountHolderName ||
															!payoutForm.accountNumber ||
															!payoutForm.ifscCode
														}
													>
														{payoutAccount
															? t("project.payments.updateAccount")
															: t("project.payments.saveAccount")}
													</StudioButton>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						</FloatPanel>
					</section>
				)}

				{/* Logout */}
				<FloatPanel elevation={1} padding={20}>
					<div className="space-y-4">
						{showLogoutConfirm ? (
							<div className="space-y-4">
								<p className="text-sm text-foreground">
									{t("settings.logoutConfirm")}
								</p>
								<div className="flex gap-3">
									<StudioButton
										variant="quiet"
										size="sm"
										onClick={() => setShowLogoutConfirm(false)}
										disabled={isLoggingOut}
									>
										{t("common.cancel")}
									</StudioButton>
									<StudioButton
										variant="primary"
										size="sm"
										onClick={handleLogout}
										disabled={isLoggingOut}
										style={{
											background: "var(--color-dashboard-danger-fill)",
											color: "var(--color-dashboard-on-fill)",
										}}
									>
										<LogOut className="h-4 w-4" />
										{t("common.logOut")}
									</StudioButton>
								</div>
							</div>
						) : (
							<StudioButton
								variant="quiet"
								onClick={() => setShowLogoutConfirm(true)}
								className="w-full text-[var(--color-dashboard-pill-risk)] hover:text-[var(--color-dashboard-pill-risk)] border-[var(--color-dashboard-pill-risk-bg)]"
								style={{ color: "var(--color-dashboard-pill-risk)" }}
							>
								<LogOut className="h-4 w-4" />
								{t("common.logOut")}
							</StudioButton>
						)}
					</div>
				</FloatPanel>
			</div>

			<CollectPhoneModal
				open={phoneModalOpen}
				onOpenChange={setPhoneModalOpen}
				initialPhone={user?.phone ?? ""}
			/>
		</div>
	);
}
